import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { learningStructureState } from "state/learningStructureState";
import { useAccount } from "@azure/msal-react";
import { getTestReport } from "services/lrsService";
import { ItemTypeKnowledge, ItemTestResultItem, ItemTestResults } from "services/reportingService";
import { ReferenceKey } from "@STRSERVICE/optima-component-library";
import { wait } from "utils/waitPromise";

interface IUseTestResult {
  /** Use this to get the results for a specific acticity */
  referenceKey?: ReferenceKey;
  /** Use this to get the result for a specific test */
  testId?: string;
  /** Use this to retry the request (since the result may not be generated instantly) */
  retry?: boolean;
}

/**
 * Custom hook to fetch and manage test results based on a reference key and test ID.
 *
 * @param {string} [referenceKey] - The reference key to identify the test results.
 * @param {string} [testId] - The ID of the specific test to filter results.
 *
 * @returns {ItemTestResultItem[] | undefined} testResults - The list of test results.
 * @returns {ItemTestResultItem | undefined} testResult - The specific test result filtered by testId.
 *
 * @example
 * const { testResults, testResult } = useTestResult({ referenceKey: 'someKey', testId: 'someTestId', retry: true });
 */
export const useTestResult = ({ referenceKey, testId, retry }: Partial<IUseTestResult> = {}) => {
  const account = useAccount();
  const [testResults, setTestResults] = useState<undefined | ItemTestResultItem[]>();
  const [testResult, setTestResult] = useState<undefined | ItemTestResultItem>();
  const [retryAttempt, setRetryAttempt] = useState(retry ? 1 : undefined);
  const learningStructure = useRecoilValue(learningStructureState);

  useEffect(() => {
    if (!account || !learningStructure) return;
    let key: any = referenceKey;

    // Get education root if no referenceKey
    if (!referenceKey) key = learningStructure?.find((x) => x.parentId === 0)?.activityKey;

    const getResults = async () => {
      const testReport = await getTestReport(account.localAccountId, key);
      const testResults = testReport.items.filter(
        (item: any) => item.type === ItemTypeKnowledge.TestResults
      ) as ItemTestResults[];

      let results = testResults.length ? testResults[0].items : [];

      // Filter result by testId
      if (testId) {
        const result = results.filter((result) => result.statementRef === testId)[0];

        // If result not found, retry
        if (!result && retryAttempt !== undefined && retryAttempt <= 5) {
          console.log("Retrying test result fetch attempt", retryAttempt);
          await wait(1000);
          setRetryAttempt((attempt) => (attempt ? attempt + 1 : 1));
          return;
        }

        setTestResult(result);
        setRetryAttempt(undefined);
      }

      // Sort by finished date descending
      results.sort((a, b) => new Date(b.finished).getTime() - new Date(a.finished).getTime());

      setTestResults(results);
    };

    getResults();
  }, [account, learningStructure, referenceKey, testId, retryAttempt]);

  return { testResults, testResult };
};
